import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// bubbleが隣接行の入力欄を覆い続けないよう、表示後に自動でフェードアウトさせる
const BUBBLE_DISPLAY_MS = 5000;
const BUBBLE_FADE_MS = 800;

interface FormErrorProps {
  message?: string;
  // bubble用。同じ文言のまま再検証されても再表示できるよう、検証のたびに作り直されるエラー自体を受け取る
  error?: { message?: string };
  variant?: 'default' | 'bubble';
  // bubbleを表示後に自動でフェードアウトさせるか。
  // onChange検証でエラー中は保存ボタンを押せない画面では、同じ内容の再検証だとRHFが再描画せず
  // 再表示されないため、falseにしてエラー中は表示し続ける
  autoHide?: boolean;
}

export const FormError = ({
  message: messageProp,
  error,
  variant = 'default',
  autoHide = true,
}: FormErrorProps) => {
  const message = messageProp ?? error?.message;
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [bubblePhase, setBubblePhase] = useState<'shown' | 'fading' | 'hidden'>('shown');

  useEffect(() => {
    setBubblePhase('shown');
    if (variant !== 'bubble' || !message || !autoHide) return;

    const fadeTimer = setTimeout(() => setBubblePhase('fading'), BUBBLE_DISPLAY_MS);
    const hideTimer = setTimeout(() => setBubblePhase('hidden'), BUBBLE_DISPLAY_MS + BUBBLE_FADE_MS);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  }, [variant, message, error, autoHide]);

  useEffect(() => {
    if (variant !== 'bubble' || !message || !anchorRef.current) {
      setPosition(null);
      return;
    }

    const updatePosition = () => {
      if (anchorRef.current) {
        const rect = anchorRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom + 4,
          left: rect.left,
        });
      }
    };

    updatePosition();
    // スクロール・リサイズ時に位置を更新
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [variant, message]);

  if (!message) return null;

  if (variant === 'default') {
    return <p className="mt-1 text-sm text-red-600">{message}</p>;
  }

  // bubbleバリアント: アンカー + ポータルでバブルをレンダリング
  return (
    <>
      <span ref={anchorRef} className="absolute inset-0 pointer-events-none" />
      {position &&
        bubblePhase !== 'hidden' &&
        createPortal(
          <div
            // 隣接行の入力欄に重なってもクリックを透過させ、修正できるようにする
            className={clsx(
              'fixed z-50 px-2 py-1 bg-white/80 rounded shadow-lg border whitespace-nowrap flex gap-2 items-center border-red-600 pointer-events-none transition-opacity',
              bubblePhase === 'fading' && 'opacity-0',
            )}
            style={{ top: position.top, left: position.left, transitionDuration: `${BUBBLE_FADE_MS}ms` }}
          >
            <ExclamationCircleIcon className="w-6 text-red-600" />
            <div className="absolute -top-1.5 left-2 w-0 h-0 border-l-6 border-r-6 border-b-6 border-transparent border-b-red-600" />
            {message}
          </div>,
          document.body,
        )}
    </>
  );
};
